import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, formatDate, localDate } from "@mandrac/shared";
import { Button, cn, Input, Select } from "@mandrac/ui";
import { finishDateText, finishTimeText, maskDateText, maskTimeText, monthWeeks, readDateText, readTimeText } from "../lib/dateText";
import { useI18n } from "../lib/i18n";

const isCoarse = () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

/** Keeps a month view; `iso` is any day in it. */
const monthOf = (iso: string) => ({ year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) });
const shiftMonth = (v: { year: number; month: number }, by: number) => {
  const t = v.year * 12 + (v.month - 1) + by;
  return { year: Math.floor(t / 12), month: (t % 12) + 1 };
};
/** The same day in a month `by` months away, or that month's last day. */
const sameDayIn = (iso: string, by: number) => {
  const v = shiftMonth(monthOf(iso), by);
  const last = new Date(Date.UTC(v.year, v.month, 0)).getUTCDate();
  return `${v.year}-${String(v.month).padStart(2, "0")}-${String(Math.min(Number(iso.slice(8)), last)).padStart(2, "0")}`;
};

const DAY_IN_TAB_ORDER = 'button[tabindex="0"]';

function Calendar({
  value,
  min,
  max,
  focusTick,
  clearable,
  onPick,
}: {
  value: string;
  min?: string;
  max?: string;
  /** goes up each time the keyboard asks to enter the calendar */
  focusTick: number;
  clearable: boolean;
  onPick: (iso: string) => void;
}) {
  const { t, locale } = useI18n();
  const today = localDate(Date.now());
  const [cursor, setCursor] = React.useState(value || today);
  const [view, setView] = React.useState(() => monthOf(value || today));
  // typing in the field moves the calendar along
  React.useEffect(() => {
    if (!value) return;
    setCursor(value);
    setView(monthOf(value));
  }, [value]);
  const grid = React.useRef<HTMLTableElement>(null);
  const focusDay = () => grid.current?.querySelector<HTMLElement>(DAY_IN_TAB_ORDER)?.focus();
  // only for a calendar that is already open: a new one is entered from onOpenAutoFocus
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (mounted.current && focusTick) focusDay();
    mounted.current = true;
  }, [focusTick]);
  // the arrow keys move the focus along with the day, also into another month
  const wantFocus = React.useRef(false);
  React.useEffect(() => {
    if (!wantFocus.current) return;
    wantFocus.current = false;
    focusDay();
  }, [cursor]);
  const moveTo = (iso: string) => {
    wantFocus.current = true;
    setCursor(iso);
    setView(monthOf(iso));
  };
  const allowed = (iso: string) => (!min || iso >= min) && (!max || iso <= max);
  const months = React.useMemo(() => Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(Date.UTC(2026, i, 1))), [locale]);
  // 2026-01-05 is a Monday
  const weekdays = React.useMemo(() => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(Date.UTC(2026, 0, 5 + i))), [locale]);
  const years = React.useMemo(() => {
    const now = Number(today.slice(0, 4));
    const from = Math.min(view.year, now - 10);
    const to = Math.max(view.year, now + 10);
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [today, view.year]);
  const weeks = monthWeeks(view.year, view.month);
  const inView = cursor.slice(0, 7) === `${view.year}-${String(view.month).padStart(2, "0")}`;
  const tabbable = inView ? cursor : `${view.year}-${String(view.month).padStart(2, "0")}-01`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" className="shrink-0" aria-label={t("common.previousMonth")} onClick={() => setView(shiftMonth(view, -1))}>
          <ChevronLeft size={18} aria-hidden="true" />
        </Button>
        <Select className="min-w-0 flex-1 px-1" aria-label={t("common.month")} value={view.month} onChange={(e) => setView({ ...view, month: Number(e.target.value) })}>
          {months.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </Select>
        <Select className="w-20 shrink-0 px-1" aria-label={t("common.year")} value={view.year} onChange={(e) => setView({ ...view, year: Number(e.target.value) })}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Button size="icon" variant="ghost" className="shrink-0" aria-label={t("common.nextMonth")} onClick={() => setView(shiftMonth(view, 1))}>
          <ChevronRight size={18} aria-hidden="true" />
        </Button>
      </div>
      <table
        ref={grid}
        role="grid"
        aria-label={`${months[view.month - 1]} ${view.year}`}
        className="w-full table-fixed border-collapse text-14"
        onKeyDown={(e) => {
          const by = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
          if (by !== undefined) {
            e.preventDefault();
            moveTo(addDays(tabbable, by));
          } else if (e.key === "PageUp" || e.key === "PageDown") {
            e.preventDefault();
            moveTo(sameDayIn(tabbable, e.key === "PageUp" ? -1 : 1));
          }
        }}
      >
        <thead>
          <tr>
            {weekdays.map((d) => (
              <th key={d} scope="col" className="pb-1 text-center text-13 font-semibold text-muted">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((iso) => {
                const selected = iso === value;
                const outside = Number(iso.slice(5, 7)) !== view.month;
                return (
                  <td key={iso} className="p-0.5">
                    <button
                      type="button"
                      tabIndex={iso === tabbable ? 0 : -1}
                      disabled={!allowed(iso)}
                      aria-label={formatDate(iso)}
                      aria-pressed={selected}
                      aria-current={iso === today ? "date" : undefined}
                      onClick={() => onPick(iso)}
                      className={cn(
                        "pressable flex aspect-square w-full items-center justify-center rounded-btn disabled:cursor-not-allowed disabled:opacity-40",
                        selected ? "bg-primary font-bold text-on-primary" : outside ? "text-muted" : "text-ink",
                        !selected && iso === today && "border border-primary font-bold",
                      )}
                    >
                      {Number(iso.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-between gap-2">
        <Button size="sm" disabled={!allowed(today)} onClick={() => onPick(today)}>
          {t("common.today")}
        </Button>
        {clearable && value ? (
          <Button size="sm" variant="ghost" onClick={() => onPick("")}>
            {t("common.clear")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A date field that shows and takes the Croatian order (04.11.2026.) in every browser. A native date field
 * follows the language of the browser or the system, and the page has no say in that, so an American browser
 * showed month first. `value` and `onChange` use ISO dates, "" when empty. Type it, or pick it from the calendar
 * (Monday first); on a touch screen the calendar opens instead of the keyboard.
 */
export const DateInput = React.forwardRef<
  HTMLInputElement,
  {
    id?: string;
    value: string;
    onChange: (iso: string) => void;
    min?: string;
    max?: string;
    required?: boolean;
    /** off for a field that must always hold a date */
    clearable?: boolean;
    /** the larger field of the dock, with no calendar icon: the whole field opens it */
    dock?: boolean;
    className?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean;
  }
>(function DateInput({ value, onChange, min, max, clearable = true, dock, className, ...rest }, ref) {
  const { t } = useI18n();
  const [text, setText] = React.useState(formatDate(value));
  const [left, setLeft] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [focusTick, setFocusTick] = React.useState(0);
  const shown = React.useRef(value);
  const anchor = React.useRef<HTMLDivElement>(null);
  const input = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => input.current!);
  React.useEffect(() => {
    if (value === shown.current) return;
    shown.current = value;
    setText(formatDate(value));
    setLeft(false);
  }, [value]);
  const emit = (iso: string) => {
    if (iso === shown.current) return;
    shown.current = iso;
    onChange(iso);
  };
  // Text that is not a whole, real date is no date: the form holds "", it does not send (the browser's own check stops
  // it), and once the worker leaves the field it says so. It is never swapped for the date that was there before.
  const bad = text.trim() !== "" && readDateText(text) === null;
  React.useLayoutEffect(() => {
    input.current?.setCustomValidity(bad ? t("error.invalidDate") : "");
  }, [bad, t]);
  const finish = () => {
    setLeft(true);
    if (!text.trim()) return;
    const iso = finishDateText(text, Number(localDate(Date.now()).slice(0, 4)));
    if (iso) {
      emit(iso);
      setText(formatDate(iso));
    }
  };
  /** Open the calendar; `enter` also takes the focus into it, for the keyboard. */
  const show = (enter: boolean) => {
    setFocusTick((n) => (enter ? n + 1 : 0));
    setOpen(true);
  };
  const showBad = left && bad;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Popover.Anchor asChild>
          <div ref={anchor} className="relative min-w-0">
            <Input
              {...rest}
              aria-invalid={rest["aria-invalid"] || showBad || undefined}
              ref={input}
              dock={dock}
              type="text"
              // a touch screen gets the calendar instead of a keyboard that covers it
              inputMode={isCoarse() ? "none" : "numeric"}
              autoComplete="off"
              placeholder={t("common.datePlaceholder")}
              value={text}
              className={cn(dock ? "px-3 text-17 font-semibold tracking-normal" : "pr-10", className)}
              onClick={() => show(false)}
              onChange={(e) => {
                const raw = e.target.value;
                // a pasted ISO date goes straight in
                const next = /^\d{4}-\d{2}-\d{2}$/.test(raw.trim()) ? formatDate(raw.trim()) : maskDateText(raw);
                setText(next);
                setLeft(false);
                emit(next ? (readDateText(next) ?? "") : "");
              }}
              onBlur={finish}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  show(true);
                } else if (e.key === "Enter") {
                  // Enter may send the form: what it sends and what the field shows must be the same
                  finish();
                }
              }}
            />
            {dock ? null : (
              <button
                type="button"
                tabIndex={-1}
                aria-label={t("common.pickDate")}
                onClick={() => (open ? setOpen(false) : show(false))}
                className="pressable absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-btn text-primary"
              >
                <CalendarDays size={18} aria-hidden="true" />
              </button>
            )}
          </div>
        </Popover.Anchor>
        {showBad ? (
          <span role="alert" className="text-13 font-semibold text-danger-text">
            {t("error.invalidDate")}
          </span>
        ) : null}
      </div>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          aria-label={t("common.pickDate")}
          className="z-[60] w-[min(20.5rem,calc(100vw-16px))] rounded-panel border border-border bg-surface p-2 text-ink shadow-lg outline-none pointer-fine:w-72"
          // The field keeps the focus, so that typing goes on while the calendar shows; only the arrow key enters it.
          // It has to happen here: until this moment the focus trap of a dialog around the field is on and takes the focus back.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (focusTick) (e.currentTarget as HTMLElement).querySelector<HTMLElement>(DAY_IN_TAB_ORDER)?.focus();
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchor.current?.contains(e.target as Node)) e.preventDefault();
          }}
        >
          <Calendar
            value={value}
            min={min}
            max={max}
            focusTick={focusTick}
            clearable={clearable}
            onPick={(iso) => {
              emit(iso);
              setText(formatDate(iso));
              setLeft(false);
              setOpen(false);
              input.current?.focus();
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});

/**
 * Date and time for a form that holds "2026-11-04T14:30" ("" when empty, or while either part is not whole): the date
 * field above, and the time as text in 24 hours, because a native time field shows AM and PM in an American browser.
 */
export function DateTimeInput({
  value,
  onChange,
  id,
  required,
  ...rest
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const { t } = useI18n();
  const [date, setDate] = React.useState(value.slice(0, 10));
  const [text, setText] = React.useState(value.slice(11, 16));
  const [left, setLeft] = React.useState(false);
  const shown = React.useRef(value);
  const timeInput = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (value === shown.current) return;
    shown.current = value;
    setDate(value.slice(0, 10));
    setText(value.slice(11, 16));
    setLeft(false);
  }, [value]);
  const emit = (d: string, time: string) => {
    const h = readTimeText(time);
    const next = d && h ? `${d}T${h}` : "";
    if (next === shown.current) return;
    shown.current = next;
    onChange(next);
  };
  const bad = !!date && readTimeText(text) === null;
  React.useLayoutEffect(() => {
    timeInput.current?.setCustomValidity(bad ? t("error.invalidTime") : "");
  }, [bad, t]);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-start gap-2">
        <DateInput
          {...rest}
          id={id}
          required={required}
          className="min-w-0"
          value={date}
          onChange={(d) => {
            setDate(d);
            // a new date with no time yet starts at noon, in plain sight; a time already there stays
            const time = d && !text.trim() ? "12:00" : text;
            setText(time);
            emit(d, time);
          }}
        />
        <Input
          ref={timeInput}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={t("common.time")}
          aria-invalid={(left && bad) || undefined}
          placeholder={t("common.timePlaceholder")}
          className="w-20 shrink-0"
          value={text}
          onChange={(e) => {
            const next = maskTimeText(e.target.value);
            setText(next);
            setLeft(false);
            emit(date, next);
          }}
          onBlur={() => {
            setLeft(true);
            const h = finishTimeText(text);
            if (h) {
              setText(h);
              emit(date, h);
            }
          }}
        />
      </div>
      {left && bad ? (
        <span role="alert" className="text-13 font-semibold text-danger-text">
          {t("error.invalidTime")}
        </span>
      ) : null}
    </div>
  );
}
